import { useEffect } from 'react'

import { HoverBubble } from './HoverBubble'
import { useHover } from './useHover'
import {
  OVERFLOW_BUBBLE_CLASS,
  classifyNativeTitleHover,
  type NativeTitleHoverProfile,
} from './nativeTitleHelpers'

export type { NativeTitleHoverProfile }

const NATIVE_ATTR = 'data-native-title'
const OPT_OUT_ATTR = 'data-hover-native'

export function GlobalNativeTitleUpgrader() {
  const immediateHover = useHover({ variant: 'label', placement: 'top', speed: 'immediate', group: 'globalNativeTitleImmediate' })
  const delayedHover = useHover({ variant: 'label', placement: 'top', speed: 'delayed', bubbleClassName: OVERFLOW_BUBBLE_CLASS })
  const { show: showImmediate, hide: hideImmediate, bubbleProps: immediateBubbleProps } = immediateHover
  const { show: showDelayed, hide: hideDelayed, bubbleProps: delayedBubbleProps } = delayedHover

  useEffect(() => {
    if (typeof document === 'undefined') return
    let currentAnchor: HTMLElement | null = null
    let currentAnchorCleanup: (() => void) | null = null

    const shouldSkip = (el: HTMLElement) => {
      if (el.getAttribute(OPT_OUT_ATTR) === 'true') return true
      if (el instanceof HTMLInputElement && el.hasAttribute('pattern')) return true
      return false
    }

    // ---- Layer 1: normalize every `title` attribute out of the DOM ----
    const normalize = (el: HTMLElement) => {
      const value = el.getAttribute('title')
      if (value == null) return
      if (shouldSkip(el)) return
      if (!value.length) {
        el.removeAttribute('title')
        return
      }
      el.setAttribute(NATIVE_ATTR, value)
      // Preserve accessibility: if the element doesn't already have an
      // aria-label, promote the title to one so screen readers still
      // announce it.
      if (!el.hasAttribute('aria-label')) {
        el.setAttribute('aria-label', value)
        el.setAttribute('data-native-title-aria', 'true')
      }
      el.removeAttribute('title')
    }

    const normalizeSubtree = (root: Element) => {
      if (root instanceof HTMLElement && root.hasAttribute('title')) normalize(root)
      root.querySelectorAll<HTMLElement>('[title]').forEach(normalize)
    }

    // Initial pass over the whole document.
    normalizeSubtree(document.body)

    const normalizer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'title') {
          const target = mutation.target
          if (target instanceof HTMLElement) normalize(target)
          continue
        }
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node instanceof Element) normalizeSubtree(node)
          })
        }
      }
    })
    normalizer.observe(document.body, {
      attributes: true,
      attributeFilter: ['title'],
      subtree: true,
      childList: true,
    })

    // ---- Layer 2: open / close the premium bubble ----
    const closeCurrent = () => {
      currentAnchorCleanup?.()
      currentAnchorCleanup = null
      currentAnchor = null
      hideImmediate()
      hideDelayed()
    }

    const findAnchor = (start: Element | null): HTMLElement | null => {
      let node: Element | null = start
      while (node && node !== document.body) {
        if (node instanceof HTMLElement && node.getAttribute(NATIVE_ATTR)) return node
        node = node.parentElement
      }
      return null
    }

    const openFor = (el: HTMLElement) => {
      const title = el.getAttribute(NATIVE_ATTR)
      if (!title) return
      currentAnchor = el
      const profile = classifyNativeTitleHover(el, title)
      if (profile.speed === 'immediate') {
        hideDelayed()
        showImmediate({ anchor: el, content: { title } })
      } else {
        hideImmediate()
        showDelayed({ anchor: el, content: { title } })
      }

      const onLeave = () => closeCurrent()
      const onDetach = new MutationObserver(() => {
        if (currentAnchor && !currentAnchor.isConnected) closeCurrent()
      })
      el.addEventListener('mouseleave', onLeave)
      el.addEventListener('pointerleave', onLeave)
      el.addEventListener('click', onLeave, true)
      onDetach.observe(document.body, { childList: true, subtree: true })
      currentAnchorCleanup = () => {
        el.removeEventListener('mouseleave', onLeave)
        el.removeEventListener('pointerleave', onLeave)
        el.removeEventListener('click', onLeave, true)
        onDetach.disconnect()
      }
    }

    const onOver = (event: PointerEvent) => {
      const target = event.target instanceof Element ? event.target : null
      const el = findAnchor(target)
      if (!el) {
        if (currentAnchor) closeCurrent()
        return
      }
      if (shouldSkip(el)) return
      if (el === currentAnchor) return
      if (currentAnchor && currentAnchor !== el) closeCurrent()
      openFor(el)
    }

    // Belt-and-braces hit-test on every pointermove — catches the cases
    // where mouseleave was skipped (rapid movement between elements, React
    // portal boundaries, canvas transforms that steal events).
    const onMove = (event: PointerEvent) => {
      if (!currentAnchor) return
      const el = document.elementFromPoint(event.clientX, event.clientY)
      if (el && currentAnchor.contains(el)) return
      closeCurrent()
    }

    const onWindowLeave = () => {
      if (currentAnchor) closeCurrent()
    }

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && currentAnchor) closeCurrent()
    }

    const onRouteChange = () => closeCurrent()

    document.addEventListener('pointerover', onOver, true)
    document.addEventListener('pointermove', onMove, true)
    document.addEventListener('keydown', onKey, true)
    window.addEventListener('blur', onWindowLeave)
    window.addEventListener('popstate', onRouteChange)
    document.addEventListener('mouseleave', onWindowLeave)

    const origPush = window.history.pushState.bind(window.history)
    const origReplace = window.history.replaceState.bind(window.history)
    window.history.pushState = function patchedPushState(...args) {
      const result = origPush(...(args as Parameters<typeof origPush>))
      onRouteChange()
      return result
    }
    window.history.replaceState = function patchedReplaceState(...args) {
      const result = origReplace(...(args as Parameters<typeof origReplace>))
      onRouteChange()
      return result
    }

    return () => {
      document.removeEventListener('pointerover', onOver, true)
      document.removeEventListener('pointermove', onMove, true)
      document.removeEventListener('keydown', onKey, true)
      window.removeEventListener('blur', onWindowLeave)
      window.removeEventListener('popstate', onRouteChange)
      document.removeEventListener('mouseleave', onWindowLeave)
      window.history.pushState = origPush
      window.history.replaceState = origReplace
      normalizer.disconnect()
      currentAnchorCleanup?.()
      currentAnchor = null
      hideImmediate()
      hideDelayed()
    }
  }, [showImmediate, showDelayed, hideImmediate, hideDelayed])

  return (
    <>
      <HoverBubble {...immediateBubbleProps} />
      <HoverBubble {...delayedBubbleProps} />
    </>
  )
}
